import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Tab, Tabs } from '@mui/material';

import { checkExists, getParticipantURL } from '../../../api/commonHTTP';
import { loadContractDetails } from '../../../store/directusService';
import {
    clearAppointmentSectionHeadings,
    clearParticipantSectionHeadings,
    createParticipantSectionHeadings,
    loadAppointmentSectionHeadings,
    loadParticipantSectionHeadings,
    updateParticipantSectionHeadings
} from '../../../store/participantService';
import { POLARIS_VICTORIA_BLUE, POLARIS_WHITE } from '../../../themes/theme';
import { hasRole, LOCAL_ADMIN, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import SingleSelect from '../../formElements/SingleSelect';
import NotFound from '../../notFound/NotFound';
import AdminManagement from '../AdminManagement';
import ConfigureServiceAdmin from '../configureServices/ConfigureServiceAdmin';

import { checkForContract, initialErrorState, validate } from './validateConfigureContract';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const SWITCH_MESSAGE = 'Must be switched on while the Appointments corresponding section is on';

const ConfigureContract = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const acceptedRoles = [SUPERUSER, QUALITY];
    const NO_SELECTION = 'no selection';
    const initialState = {
        participantBarriers: false,
        actionPlanGoals: false,
        serviceStandards: false,
        wellbeing: false,
        workRequirements: false,
        commissionerCommunications: false,
        courses: false,
        supportedJobSearch: false,
        workHistory: false,
        education: false,
        submissions: false,
        placements: false,
        healthInformation: false,
        circumstances: false,
        employability: false,
        environment: false,
        connectToWork: false,
        ltcp: false,
        t2dr: false,
        ndppData: false,
        bsop: false,
        financialRequests: false,
        assetRequests: false,
        disclaimer: false
    };

    const [initialHeadings, setInitialHeadings] = useState({});
    const [isHeading, setIsHeading] = useState(initialState);
    const [contractId, setContractId] = useState('');
    const [showPanel, setShowPanel] = useState('contractDetails');
    const [value, setValue] = useState(0);
    const [switchMessages, setSwitchMessages] = useState({});
    const [errors, setErrors] = useState(initialErrorState);
    const [isDisabled, setIsDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const contractDetails = useSelector((state) => state.entities.directusService.contractDetails);
    const participantSectionHeadings = useSelector(
        (state) => state.entities.participantService.participantSectionHeadings
    );
    const appointmentSectionHeadings = useSelector(
        (state) => state.entities.participantService.appointmentSectionHeadings
    );

    //  USE EFFECTS
    useEffect(() => {
        dispatch(loadContractDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (contractId !== '' && contractId !== NO_SELECTION) {
            if (Object.keys(participantSectionHeadings).length !== 0) {
                // eslint-disable-next-line no-unused-vars
                const { id, contractId, ...rest } = participantSectionHeadings;
                setInitialHeadings((prev) => ({ ...prev, ...rest }));
                setIsHeading((prev) => ({ ...prev, ...rest }));
            } else setIsHeading(initialState);
        } else setIsHeading(initialState);
    }, [participantSectionHeadings, contractId]);

    useEffect(() => {
        // if appointment toggles are toggled on - add message to switch
        if (
            !contractId ||
            contractId === NO_SELECTION ||
            Object.keys(participantSectionHeadings).length < 1 ||
            participantSectionHeadings.contractId !== appointmentSectionHeadings.contractId
        )
            return;
        let updates = [];
        if (appointmentSectionHeadings.courses) updates = [...updates, 'courses'];
        if (appointmentSectionHeadings.disclaimer) updates = [...updates, 'disclaimer'];
        if (appointmentSectionHeadings.supportedJobSearch)
            updates = [...updates, 'supportedJobSearch'];
        if (appointmentSectionHeadings?.barriers) updates = [...updates, 'participantBarriers'];
        if (appointmentSectionHeadings?.actionPlan) updates = [...updates, 'actionPlanGoals'];
        if (appointmentSectionHeadings?.request)
            updates = [...updates, 'financialRequests', 'assetRequests'];
        const sectionHeadingMessages = updates.reduce(
            (acc, cur) => ({ ...acc, [cur]: SWITCH_MESSAGE }),
            {}
        );
        setSwitchMessages(sectionHeadingMessages);
        // for backward compatibility
        const offSwitches = updates.filter((el) => !participantSectionHeadings[el]);
        if (offSwitches.length > 0) {
            const updates = offSwitches.reduce((acc, cur) => ({ ...acc, [cur]: true }), {});
            dispatch(
                updateParticipantSectionHeadings({
                    contractId,
                    ...participantSectionHeadings,
                    ...updates
                })
            );
        }
    }, [appointmentSectionHeadings, participantSectionHeadings, contractId]);

    useEffect(() => {
        setIsDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const cleanUp = () => {
        setContractId('');
        dispatch(clearParticipantSectionHeadings());
        dispatch(clearAppointmentSectionHeadings());
    };

    // EVENT HANDLERS
    const onContractChange = (chosenId) => {
        clearError('contract');
        if (chosenId === NO_SELECTION) return;
        setContractId(chosenId);
        if (chosenId) {
            dispatch(loadParticipantSectionHeadings(chosenId));
            dispatch(loadAppointmentSectionHeadings(chosenId));
        }
    };

    const onChangeTab = (event, newValue) => {
        setValue(newValue);
        newValue === 0 ? setShowPanel('contractDetails') : setShowPanel('contractServices');
    };

    const onCheckForContract = () => {
        const newErrors = checkForContract(contractId, errors);
        setErrors(newErrors);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (showPanel !== 'contractDetails') return;
        const { newErrors } = validate(contractId, initialHeadings, isHeading);
        setErrors(newErrors);
        if (Object.values(newErrors).some((el) => el.error)) return;
        let data;
        if (
            await checkExists(
                getParticipantURL() + 'rest/participant-section-heading/exists/' + contractId
            )
        ) {
            data = {
                id: participantSectionHeadings.id,
                contractId,
                ...isHeading
            };
            dispatch(updateParticipantSectionHeadings(data));
        } else {
            data = { id: null, contractId, ...isHeading };
            dispatch(createParticipantSectionHeadings(data));
        }
    };

    // RENDER
    if (!hasRole(acceptedRoles, roles)) return <NotFound />;

    return (
        <>
            <div className={form.formWrapper} data-testid="form_start">
                <form className={form.form} onSubmit={onSubmit}>
                    <h2 className={app.mainHeading}>Configure Contract</h2>
                    <div className={form.formSection}>
                        <SingleSelect
                            id={'contract'}
                            label={'Contract'}
                            placeholder={'Select contract...'}
                            mandatory={true}
                            menuItems={contractDetails || []}
                            selectedId={contractId || ''}
                            selected={contractDetails?.find((el) => el.id === contractId) || {}}
                            error={errors?.contract}
                            onChange={(contractId) => onContractChange(contractId)}
                        />
                    </div>
                    <div className={form.formSection}>
                        <Tabs
                            onChange={onChangeTab}
                            value={value}
                            hidden={!hasRole([SUPERUSER, LOCAL_ADMIN], roles)}
                            slotProps={{ transition: { mountOnEnter: true } }}
                            sx={{
                                marginBottom: '16px',
                                '& button:hover': {
                                    backgroundColor: `${POLARIS_VICTORIA_BLUE}`,
                                    color: `${POLARIS_WHITE}`
                                },
                                '& button.Mui-selected': {
                                    backgroundColor: `${POLARIS_VICTORIA_BLUE}`,
                                    color: `${POLARIS_WHITE}`
                                }
                            }}>
                            <Tab
                                label="Contract Details"
                                sx={{
                                    border: `2px solid ${POLARIS_VICTORIA_BLUE}`,
                                    margin: '0px 4px',
                                    borderRadius: '4px'
                                }}
                                value={0}
                            />
                            <Tab
                                label="Contract Services"
                                sx={{
                                    border: `2px solid ${POLARIS_VICTORIA_BLUE}`,
                                    margin: '0px 4px',
                                    borderRadius: '4px'
                                }}
                                value={1}
                            />
                        </Tabs>
                    </div>
                    <div hidden={showPanel === 'contractServices'}>
                        <Button
                            id="updateSectionHeadings"
                            content="UPDATE SECTION HEADINGS"
                            disabled={!hasRole(acceptedRoles, roles) || isDisabled}
                            error={errors?.button}
                            clearError={() => clearError('button')}
                        />
                    </div>
                </form>
            </div>

            {showPanel === 'contractDetails' ? (
                <AdminManagement
                    isHeading={isHeading}
                    contractId={contractId}
                    onHeadingChange={(values) => setIsHeading(values)}
                    switchMessages={switchMessages}
                />
            ) : (
                <ConfigureServiceAdmin
                    contractId={contractId}
                    onCheckForContract={onCheckForContract}
                />
            )}
        </>
    );
};

export default ConfigureContract;
